<template>
  <span v-if="errorMessage">
    <div class="error-container">
      <div class="d-flex justify-end mb-2">
        <v-btn icon small title="Close" @click="handleDismiss">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <vs-alert type="error">{{ errorMessage }}</vs-alert>
    </div>
  </span>
  <span v-else-if="!isLoading && sideSheetType !== undefined">
    <FieldViewJobPanelProvision v-if="showFieldViewProvision" :job-number="id" />
    <Ticket
      v-if="sideSheetType === SideSheetType.Editable || props.id === 'new'"
      :id="props.id"
      ref="editableSideSheet"
      :on-save-route="props.onSaveRoute"
      :hub-id="props.hubId"
      :location-id="props.locationId"
      @job-saved="handleJobSaved"
      @dismiss="handleDismiss"
      @back-to-readonly="handleBackToReadonly"
    />
    <ReadonlyJobForm
      v-else-if="sideSheetType === SideSheetType.Readonly"
      :id="props.id"
      :hub-id="props.hubId"
      :location-id="props.locationId"
      @dismiss="handleDismiss"
      @job-saved="handleJobSaved"
      @on-click-edit="sideSheetType = SideSheetType.Editable"
    />
    <ClaimJobForm
      v-else-if="sideSheetType === SideSheetType.ClaimJob"
      :number="props.id"
      @dismiss="handleDismiss"
      @claimed="handleJobClaimed"
    />
  </span>
  <div v-else class="d-flex align-center justify-center" style="height: 100vh">
    <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { useStore } from 'vuex-composition-helpers'
import Job, { JobStatus } from '@/models/job'
import { priorities, priorityMap } from '@/utils/constants'
import Ticket from '../tickets/Ticket.vue'
import type { Dictionary, Route } from 'vue-router/types/router'
import ReadonlyJobForm from '../tickets/ReadonlyJobForm.vue'
import ClaimJobForm from './ClaimJobForm.vue'
import { ALLOWED_JOB_STATUS_FOR_READONLY_UI } from '@/components/jobs/navigation'
import { useJobFormDataStore } from './jobFormDataStore'
import { useJobBottomSheetStatesStore } from './jobBottomSheetStatesStore'
import FieldViewJobPanelProvision from '../field-view-next/FieldViewJobPanelProvision.vue'
import { useJobNavigationGuard } from './composables/useJobNavigationGuard'
import { useParentRouteNameProvider } from './composables/useParentRouteName'
import { useCanJobBeEdited } from '../tickets/useCanJobBeEdited'
import { isFieldViewJobRouteName } from '../tickets/common/useJobMapLink'

// Route params
const route = useRoute()
const props = withDefaults(
  defineProps<{
    id: number | 'new'
    onSaveRoute?: string
    parentRouteName: string
    parentRouteParams?: Dictionary<string>
    hubId?: string
    locationId?: string | number
  }>(),
  {
    onSaveRoute: 'ticket-form',
    parentRouteParams: () => ({}),
    hubId: '',
    locationId: undefined,
  },
)

const emit = defineEmits<{
  (e: 'job-saved', data: { jobNumber: number })
}>()

// Constants
const newPriorityId = priorities.find((p) => p.name === priorityMap.New.name)?.id
const normalPriorityId = priorities.find((p) => p.name === priorityMap.Normal.name)?.id

// Component state
enum SideSheetType {
  Editable,
  Readonly,
  ClaimJob,
}
const errorMessage = ref()
const isLoading = ref(true)
const sideSheetType = ref<SideSheetType | undefined>(undefined)
const editableSideSheet = ref<InstanceType<typeof Ticket> | null>(null)
const isJobNew = computed(() => !!route.params.isJobNew)
const showFieldViewProvision = computed(
  () => props.id !== 'new' && isFieldViewJobRouteName(props.parentRouteName),
)
const isDraftChangesDetected = computed(() => {
  return editableSideSheet.value?.draftChangesDetected || false
})

// Vuex Store
const store = useStore()
const router = useRouter()
const clearTicketsError = () => {
  errorMessage.value = ''
  store.commit('tickets/CLEAR_TICKETS_ERROR')
}
const removeJobFromStore = (jobId: number) => {
  store.commit('jobs/REMOVE_JOB', jobId)
}
const getJobById = (id: number | string): Job | null => {
  return store.getters['jobs/getById'](id)
}
const fetchJob = async (id: number | string): Promise<Job> => {
  return await store.dispatch('jobs/fetchJob', id)
}
const updatePriority = async (payload: {
  jobIds: (number | string)[]
  priorityId: number | undefined
}) => {
  return await store.dispatch('jobs/updatePriority', payload)
}
const ticketsError = computed(() => store.state.tickets.error)
const canModifyPriority = computed<boolean>(() => store.getters['permissions/canModifyPriority'])
const supplementaryDataLoaded = computed(
  () => store.state.initialized && store.state.jobs.supplementaryDataLoaded,
)
const isJobSaving = computed<boolean>(() => store.getters['jobs/getSaving'])

const canJobBeEdited = useCanJobBeEdited()

// Methods
const loadJob = async (jobId: number) => {
  clearTicketsError()
  isLoading.value = true
  const fetchedJob = await fetchJob(jobId)
  if (ticketsError.value) {
    errorMessage.value = 'Job cannot be loaded.'
    isLoading.value = false
    if (ticketsError.value?.response?.status === 404) {
      removeJobFromStore(jobId)
    } else {
      // We want sentry to log when a job fails to load for reasons other than a 404
      throw new Error(ticketsError.value)
    }
    return
  }
  determineSideSheetTypeForJob(fetchedJob)

  // Update Priority
  if (
    !isJobNew.value &&
    fetchedJob.priorityId === newPriorityId &&
    fetchedJob.isNotCompleteOrCancelled &&
    !fetchedJob.isApproved &&
    !fetchedJob.isArchived &&
    canModifyPriority.value
  ) {
    await updatePriority({
      jobIds: [props.id],
      priorityId: normalPriorityId,
    })
  }
  isLoading.value = false
}

const handleDismiss = () => {
  router.push({
    name: props.parentRouteName,
    params: props.parentRouteParams,
  })
}

const handleBackToReadonly = () => {
  sideSheetType.value = SideSheetType.Readonly
}

const handleJobSaved = (data: { jobNumber: number }) => {
  emit('job-saved', data)
  const job = getJobById(data.jobNumber)
  if (!job) {
    return
  }
  determineSideSheetTypeForJob(job)
}

const determineSideSheetTypeForJob = (job: Job) => {
  if (
    job.status === JobStatus.ASSIGNED_TO_POOL &&
    store.getters['permissions/canClaimJob'](job.hubId)
  ) {
    sideSheetType.value = SideSheetType.ClaimJob
  } else if (
    ALLOWED_JOB_STATUS_FOR_READONLY_UI.includes(job.status) ||
    !canJobBeEdited(job.status, job.isArchived, job.hubId)
  ) {
    sideSheetType.value = SideSheetType.Readonly
  } else {
    sideSheetType.value = SideSheetType.Editable
  }
}

const handleJobClaimed = () => {
  // ClaimJobForm.vue refetch the job, put it in Vuex store before emit it here.
  sideSheetType.value = SideSheetType.Editable
}

const loadNewDraftJobSideSheet = async () => {
  clearTicketsError()
  if (route.query?.fromJob) {
    // Clone job
    const clonedFromJobId = parseInt(route.query?.fromJob as string, 10)
    isLoading.value = true
    await fetchJob(clonedFromJobId)
    if (ticketsError.value) {
      isLoading.value = false
      if (ticketsError.value?.response?.status === 404) {
        removeJobFromStore(clonedFromJobId)
      } else {
        // We want sentry to log when a job fails to load for reasons other than a 404
        throw new Error(ticketsError.value)
      }
      return
    }
    sideSheetType.value = SideSheetType.Editable
    isLoading.value = false
  } else {
    // New job
    sideSheetType.value = SideSheetType.Editable
    isLoading.value = false
  }
}

// Lifecycle
watch(
  [() => props.id, supplementaryDataLoaded],
  async ([newId, newSupplementaryDataLoaded], [oldId, oldSupplementaryDataLoaded]) => {
    if (!newSupplementaryDataLoaded) {
      return
    }
    if (newId !== oldId || newSupplementaryDataLoaded !== oldSupplementaryDataLoaded) {
      if (newId === 'new') {
        await loadNewDraftJobSideSheet()
      } else {
        await loadJob(newId)
      }
    }
  },
  { immediate: true },
)

const shouldAllowNavigation = (to: Route) => {
  // Skip confirmation in these cases:
  // 1. If explicitly marked as saved (when bulk creating jobs - which then navigates to project view)
  // 2. If we're actively saving
  // 3. If we are saving a new/clone job
  // Note: `to.params.isJobNew` refers to the *destination* route's param, set when navigating to a newly created job.
  // This is distinct from the local `isJobNew` computed property, which checks the *current* route.
  if (!!to.params.isJobSaved || isJobSaving.value || !!to.params.isJobNew) {
    return true
  }

  if (isDraftChangesDetected.value) {
    const confirmation = window.confirm(
      'There are unsaved changes, are you sure you want to discard them?',
    )

    if (!confirmation) {
      return false
    }
  }

  return true
}

const jobFormDataStore = useJobFormDataStore()
const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()

const clearStates = () => {
  clearTicketsError()
  jobFormDataStore.clearAll()
  jobBottomSheetStatesStore.clearJobBottomSheetStates()
}

useJobNavigationGuard({
  shouldAllowNavigation,
  shouldSkipGuard: (to) => {
    return (
      sideSheetType.value === SideSheetType.Readonly ||
      sideSheetType.value === SideSheetType.ClaimJob ||
      // Take care of the case where clicking the "Archived" button in the OLD job search UI.
      // The new job search UI doesn't have this behavior. Once sunsetting the old UI, this can be removed.
      !!to.query.archived
    )
  },
  onNavigationStart: () => {
    isLoading.value = true
    clearStates()
  },
})

useParentRouteNameProvider(computed(() => props.parentRouteName))

// Cleanup happens in proper lifecycle hook
onBeforeUnmount(() => {
  clearStates()
})
</script>

<style scoped>
.error-container {
  padding: 1.5rem;
}
</style>
